import styled, { createGlobalStyle, css } from "styled-components";

import Container from "react-modal";
import Icon from "components/icons";
import PropTypes from "prop-types";
import React from "react";

// -----------------------------------------------------------------------------------------------
// modal design specifications: modal styles i.e. margins, padding... please do not additional
// margins, padding and so on in your child components this will cause inconsistencies :(
// -----------------------------------------------------------------------------------------------
// modal position:
//  mobile/tablet:
//    docked to the bottom of the viewport
//  desktop:
//    centered in the middle of the viewport
// -----------------------------------------------------------------------------------------------
// close button positioning:
//  mobile/tablet/desktop:
//    top - 0.675rem;
//    right - 0.675rem;
// -----------------------------------------------------------------------------------------------
// outer margins:
//  mobile/tablet:
//    top - 3.75rem
//    bottom - 0
//    right/left - auto
//  desktop:
//    top/bottom/right/left - 0 auto
// -----------------------------------------------------------------------------------------------
// inner padding:
//  mobile/tablet:
//    top/bottom - 2.5rem
//    right/left - 1.5rem
//  desktop:
//    top/bottom/right/left - 2.5rem
// -----------------------------------------------------------------------------------------------
// width:
//  mobile/tablet/desktop:
//    100%
// -----------------------------------------------------------------------------------------------
// min-width:
//  mobile/tablet/desktop:
//    23.4375rem
// -----------------------------------------------------------------------------------------------
// max-width:
//  mobile/tablet/desktop:
//    66rem
// -----------------------------------------------------------------------------------------------

const ModalGlobalStyle = createGlobalStyle`
  .ReactModal__Body--open {
    overflow: hidden;
  }

  .ReactModal__Overlay {
    align-items: center;
    justify-content: flex-end;
    opacity: 0;
    transition: opacity 200ms ease-in-out;
  }

  .ReactModal__Overlay--after-open {
    opacity: 1;

    ${({ theme }) => theme.mq.lg`
      justify-content: center;
    `}
  }

  .ReactModal__Overlay--before-close {
      opacity: 0;
  }

  .ReactModal__Content {
    border-radius: 0.75rem 0.75rem 0 0;
    margin: 3.75rem auto 0;
    overflow: auto;
    padding: 0;
    position: relative;
    width: 100%;

    :focus {
      outline: 0;
    }

    ${({ theme }) => theme.mq.lg`
      margin: 0 auto;
      width: auto;
    `}
  }
`;

const settings = {
  style: {
    overlay: {
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      flexDirection: "column",
      position: "fixed",
      zIndex: 1000,
    },
  },
};

const Content = styled.div`
  background: ${({ theme }) => theme.themeColors.white};
  border-radius: 0.75rem 0.75rem 0 0;
  margin: 0 auto;
  min-width: 100vw;
  padding: 2.5rem 1.5rem;
  position: relative;
  z-index: 1001;

  ${({ theme }) => theme.mq.md`
    min-width: 100vw;
  `}

  ${({ theme }) => theme.mq.lg`
    border-radius: 0.75rem;
    padding: 2.5rem;
    max-width: 66rem;
    min-width: 20rem;
  `}

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 1px solid blue;
  }

  ${({ alignCenter }) =>
    alignCenter &&
    css`
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
    `}
`;

const CloseButton = styled.button`
  background: 0;
  border: 0;
  outline: 0;
  padding: 0;
  position: absolute;
  right: 0.675rem;
  top: 0.675rem;
  text-align: right;
  z-index: 1005;

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }
`;

// necessary for accessibility reasons
Container.setAppElement("#__next");

function Modal({
  children,
  show,
  close,
  isAlignedCenter,
  className,
  closable,
}) {
  return (
    <>
      <ModalGlobalStyle />
      <Container
        className={`goodfriend-modal ${className}`}
        isOpen={show}
        onRequestClose={close}
        {...settings}
      >
        <Content alignCenter={isAlignedCenter}>
          {closable ? (
            <CloseButton onClick={close}>
              <Icon name="close-30" color="#8F978C" />
            </CloseButton>
          ) : undefined}
          {children}
        </Content>
      </Container>
    </>
  );
}

Modal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element]),
  className: PropTypes.string,
  close: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  padding: PropTypes.string,
  margin: PropTypes.string,
  isAlignedCenter: PropTypes.bool,
  closable: PropTypes.bool,
};

Modal.defaultProps = {
  children: null,
  className: "",
  padding: undefined,
  margin: undefined,
  isAlignedCenter: false,
  closable: true,
};

export default Modal;
